import React, { useState, useEffect } from 'react';
import { Plus, Layout, FileText, Phone, BookOpen, Package, Briefcase, Grid, Trash2, Eye, Copy, Search, Filter, FileCode, Upload, Camera, Edit, Globe, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';
import TemplatePreviewModal from '../components/TemplatePreviewModal';
import HTMLImportModal from '../components/HTMLImportModal';
import VisionImportModal from '../components/VisionImportModal';
import { useAuth } from '../context/AuthContext';

const CATEGORY_ICONS = {
  landing: Layout,
  about: FileText,
  contact: Phone,
  blog: BookOpen,
  product: Package,
  portfolio: Briefcase,
  other: Grid,
};

const CATEGORY_LABELS = {
  landing: 'Landing Page',
  about: 'About Us',
  contact: 'Contact',
  blog: 'Blog Post',
  product: 'Product/Service',
  portfolio: 'Portfolio',
  other: 'Other',
};

const Templates = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'master_admin';
  const isMasterAdmin = currentUser?.role === 'master_admin';
  
  const [templates, setTemplates] = useState({ system: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showHTMLImport, setShowHTMLImport] = useState(false);
  const [showVisionImport, setShowVisionImport] = useState(false);
  const [editingSystemTemplate, setEditingSystemTemplate] = useState(null);
  const [systemTemplateForm, setSystemTemplateForm] = useState({
    name: '',
    description: '',
    category: 'landing',
    thumbnail_url: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/templates');
      setTemplates(res.data);
      
      // Auto-seed if no system templates exist
      if (res.data.system.length === 0) {
        await seedDefaultTemplates();
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultTemplates = async () => {
    try {
      const res = await api.post('/templates/seed-defaults');
      if (res.data.seeded > 0) {
        toast.success(`Loaded ${res.data.seeded} default templates`);
        // Refresh templates
        const templatesRes = await api.get('/templates');
        setTemplates(templatesRes.data);
      }
    } catch (error) {
      console.error('Failed to seed templates:', error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/templates/${templateId}`);
      setTemplates({
        ...templates,
        custom: templates.custom.filter(t => t.template_id !== templateId)
      });
      toast.success('Template deleted');
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const newTemplate = {
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        thumbnail_url: template.thumbnail_url,
        blocks: template.blocks,
        page_width: template.page_width,
        theme: template.theme,
        is_system: false,
      };
      const res = await api.post('/templates', newTemplate);
      setTemplates({
        ...templates,
        custom: [...templates.custom, res.data]
      });
      toast.success('Template duplicated to your library');
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  // System Template handlers (Super Admin only)
  const openEditSystemTemplate = (template) => {
    setEditingSystemTemplate(template);
    setSystemTemplateForm({
      name: template.name,
      description: template.description || '',
      category: template.category || 'other',
      thumbnail_url: template.thumbnail_url || '',
    });
  };

  const handleSaveSystemTemplate = async () => {
    try {
      if (editingSystemTemplate) {
        // Update existing template
        const res = await api.put(`/templates/${editingSystemTemplate.template_id}`, systemTemplateForm);
        setTemplates({
          ...templates,
          system: templates.system.map(t => 
            t.template_id === editingSystemTemplate.template_id ? res.data : t
          )
        });
        toast.success('System template updated');
        setEditingSystemTemplate(null);
      }
    } catch (error) {
      console.error('Failed to save system template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save system template');
    }
  };

  const handleDeleteSystemTemplate = async (templateId) => {
    try {
      await api.delete(`/templates/${templateId}`);
      setTemplates({
        ...templates,
        system: templates.system.filter(t => t.template_id !== templateId)
      });
      toast.success('System template deleted');
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete system template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete system template');
    }
  };

  const handlePromoteToSystem = async (template) => {
    try {
      // Create a new system template from an existing custom template
      const newTemplate = {
        name: template.name,
        description: template.description,
        category: template.category,
        thumbnail_url: template.thumbnail_url,
        blocks: template.blocks,
        page_width: template.page_width,
        theme: template.theme,
        is_system: true,
      };
      const res = await api.post('/templates', newTemplate);
      setTemplates({
        ...templates,
        system: [...templates.system, res.data]
      });
      toast.success('Template promoted to System Template');
    } catch (error) {
      console.error('Failed to promote template:', error);
      toast.error(error.response?.data?.detail || 'Failed to promote template');
    }
  };

  const filterTemplates = (templateList) => {
    return templateList.filter(template => {
      const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  };

  const TemplateCard = ({ template, isSystem }) => {
    const CategoryIcon = CATEGORY_ICONS[template.category] || Grid;
    
    return (
      <Card className="group hover:shadow-lg transition-all duration-200 overflow-hidden">
        <div className="relative aspect-video bg-slate-100 overflow-hidden">
          {template.thumbnail_url ? (
            <img
              src={template.thumbnail_url}
              alt={template.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
              <CategoryIcon size={48} className="text-slate-300" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPreviewTemplate(template)}
              className="mr-2"
            >
              <Eye size={16} className="mr-1" />
              Preview
            </Button>
          </div>
          {isSystem && (
            <span className="absolute top-2 left-2 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full flex items-center gap-1">
              <Globe size={12} />
              System
            </span>
          )}
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 truncate">{template.name}</h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{template.description}</p>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded capitalize">
                {CATEGORY_LABELS[template.category] || template.category}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => window.location.href = `/pages/new?template=${template.template_id}`}
            >
              Use Template
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDuplicateTemplate(template)}
              title="Duplicate to My Templates"
            >
              <Copy size={16} />
            </Button>
            {/* Master Admin actions for system templates */}
            {isSystem && isMasterAdmin && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEditSystemTemplate(template)}
                  title="Edit System Template"
                  className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                >
                  <Edit size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm({ ...template, isSystem: true })}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  title="Delete System Template"
                >
                  <Trash2 size={16} />
                </Button>
              </>
            )}
            {/* Non-system template actions */}
            {!isSystem && (
              <>
                {isSuperAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handlePromoteToSystem(template)}
                    title="Promote to System Template"
                    className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50"
                  >
                    <Globe size={16} />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm(template)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const filteredSystem = filterTemplates(templates.system);
  const filteredCustom = filterTemplates(templates.custom);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Page Templates
            </h1>
            <p className="text-slate-500 mt-1">Choose from pre-built templates or use your own saved designs</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowVisionImport(true)}
              className="gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              <Camera size={18} />
              Vision Import
              <span className="px-1.5 py-0.5 bg-white/20 text-xs rounded">AI</span>
            </Button>
            <Button 
              onClick={() => setShowHTMLImport(true)}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              <FileCode size={18} />
              Import HTML
              <span className="px-1.5 py-0.5 bg-purple-500 text-xs rounded">PRO</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All Templates ({filteredSystem.length + filteredCustom.length})</TabsTrigger>
            <TabsTrigger value="system">System Templates ({filteredSystem.length})</TabsTrigger>
            <TabsTrigger value="custom">My Templates ({filteredCustom.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            {filteredSystem.length === 0 && filteredCustom.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Layout size={48} className="text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No templates found</h3>
                  <p className="text-slate-500 text-center mt-1">Try adjusting your search or filters</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredSystem.map(template => (
                  <TemplateCard key={template.template_id} template={template} isSystem={true} />
                ))}
                {filteredCustom.map(template => (
                  <TemplateCard key={template.template_id} template={template} isSystem={false} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="system" className="mt-6">
            {filteredSystem.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Layout size={48} className="text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No system templates</h3>
                  <p className="text-slate-500 text-center mt-1">System templates will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredSystem.map(template => (
                  <TemplateCard key={template.template_id} template={template} isSystem={true} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="custom" className="mt-6">
            {filteredCustom.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Plus size={48} className="text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No custom templates yet</h3>
                  <p className="text-slate-500 text-center mt-1 mb-4">
                    Save your pages as templates or duplicate system templates to build your library
                  </p>
                  <Button onClick={() => window.location.href = '/pages'}>
                    Go to Pages
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredCustom.map(template => (
                  <TemplateCard key={template.template_id} template={template} isSystem={false} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Preview Modal */}
        {previewTemplate && (
          <TemplatePreviewModal
            template={previewTemplate}
            onClose={() => setPreviewTemplate(null)}
            onUse={() => {
              window.location.href = `/pages/new?template=${previewTemplate.template_id}`;
            }}
          />
        )}

        {/* Delete Confirmation */}
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {showDeleteConfirm?.isSystem ? 'Delete System Template' : 'Delete Template'}
              </DialogTitle>
            </DialogHeader>
            <p className="text-slate-600">
              Are you sure you want to delete &quot;{showDeleteConfirm?.name}&quot;? 
              {showDeleteConfirm?.isSystem && (
                <span className="block mt-2 text-amber-600 font-medium">
                  ⚠️ This is a system template and will be removed from all accounts.
                </span>
              )}
              This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (showDeleteConfirm?.isSystem) {
                    handleDeleteSystemTemplate(showDeleteConfirm?.template_id);
                  } else {
                    handleDeleteTemplate(showDeleteConfirm?.template_id);
                  }
                }}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* System Template Edit Modal (Super Admin Only) */}
        <Dialog open={!!editingSystemTemplate} onOpenChange={() => setEditingSystemTemplate(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="text-emerald-600" size={20} />
                Edit System Template
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name *</Label>
                <Input
                  id="template-name"
                  placeholder="e.g., Modern Landing Page"
                  value={systemTemplateForm.name}
                  onChange={(e) => setSystemTemplateForm({ ...systemTemplateForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="template-description">Description</Label>
                <Textarea
                  id="template-description"
                  placeholder="Brief description of the template..."
                  value={systemTemplateForm.description}
                  onChange={(e) => setSystemTemplateForm({ ...systemTemplateForm, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="template-category">Category</Label>
                <Select 
                  value={systemTemplateForm.category} 
                  onValueChange={(value) => setSystemTemplateForm({ ...systemTemplateForm, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="template-thumbnail">Thumbnail URL (optional)</Label>
                <Input
                  id="template-thumbnail"
                  placeholder="https://example.com/thumbnail.jpg"
                  value={systemTemplateForm.thumbnail_url}
                  onChange={(e) => setSystemTemplateForm({ ...systemTemplateForm, thumbnail_url: e.target.value })}
                />
                <p className="text-xs text-slate-500">
                  Provide a preview image URL for the template
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> To edit the template content, create a page from this template, 
                  make your changes, then save it as a new system template.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingSystemTemplate(null)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSystemTemplate}
                disabled={!systemTemplateForm.name.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Update Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* HTML Import Modal */}
        <HTMLImportModal
          open={showHTMLImport}
          onClose={() => setShowHTMLImport(false)}
          onImported={(result) => {
            // Add the new template to the custom list
            setTemplates({
              ...templates,
              custom: [...templates.custom, {
                template_id: result.template_id,
                name: result.name,
                description: `Imported HTML template`,
                category: 'other',
                blocks: result.blocks,
                is_system: false
              }]
            });
          }}
        />

        {/* Vision Import Modal */}
        <VisionImportModal
          isOpen={showVisionImport}
          onClose={() => setShowVisionImport(false)}
          onImportComplete={(result) => {
            // Add the new template to the custom list
            setTemplates({
              ...templates,
              custom: [...templates.custom, {
                template_id: result.template_id,
                name: result.name,
                description: `Vision-imported template`,
                category: 'imported',
                blocks: result.blocks,
                is_system: false
              }]
            });
            setShowVisionImport(false);
          }}
        />
      </div>
    </div>
  );
};

export default Templates;
